import type { ApiResponse, LoginResult } from '@mealdesk/shared';
import { isTokenExpiringSoon, useAuthStore } from '../stores/auth.js';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

/** Where an account on a temporary password is sent, from anywhere. */
const REDIRECT = '/change-password';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** Concurrent 401s must not each fire their own refresh. */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const { setSession, clear } = useAuthStore.getState();

  refreshInFlight ??= (async () => {
    try {
      // credentials: 'include' is what sends the refresh cookie; there is no
      // token to put in the body.
      const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      const body = (await response.json()) as ApiResponse<Omit<LoginResult, 'refreshToken'>>;
      if (!body.ok) {
        clear();
        return false;
      }
      setSession(body.data);
      return true;
    } catch {
      clear();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

type FetchOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  /** Skips the Authorization header — sign-in and link redemption. */
  anonymous?: boolean;
};

export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { body, anonymous, headers, ...rest } = options;

  if (!anonymous) {
    const { accessToken, expiresAt } = useAuthStore.getState();
    if (accessToken && isTokenExpiringSoon(expiresAt)) await refreshSession();
  }

  const send = async (): Promise<Response> => {
    const token = anonymous ? null : useAuthStore.getState().accessToken;
    const isFormData = body instanceof FormData;

    return fetch(`${BASE_URL}${path}`, {
      ...rest,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(body !== undefined && !isFormData ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
    });
  };

  let response = await send();

  if (response.status === 401 && !anonymous && (await refreshSession())) {
    response = await send();
  }

  let payload: ApiResponse<T>;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(response.status, 'INTERNAL', 'Could not read the server response');
  }

  if (!payload.ok) {
    if (response.status === 401) useAuthStore.getState().clear();
    // Signed in, but still on the password somebody else chose. Every other
    // request will be refused the same way, so there is one place to send
    // them and it is here.
    if (payload.error.code === 'PASSWORD_CHANGE_REQUIRED' && !location.pathname.startsWith(REDIRECT)) {
      location.assign(REDIRECT);
    }
    throw new ApiError(response.status, payload.error.code, payload.error.message);
  }

  return payload.data;
}
